// Какво може да се каже за доставка без да се рендира: тип документ, статус на
// плащане, състоянието на стоковата спрямо фактурата, номер, складове. Общо за
// началния екран, справката и плащането.
import type { TFunction } from "i18next";
import type {
  Delivery,
  DeliveryDocumentType,
  DeliveryPaymentMethod,
  Product,
} from "../../../../api/inventory";

// Заварен запис (NULL) се чете като фактура
export const docTypeOf = (delivery: { documentType?: DeliveryDocumentType | null }) =>
  delivery.documentType === "note" ? "note" : "invoice";

export const isDeliveryAnnulled = (delivery: Delivery) =>
  delivery.status === "annulled" || !!delivery.deletedAt;

// Ключът на плащането за филтри/хапчета. „untracked" = заварен запис отпреди
// плащането (не пълни „Неплатени" с цялата история).
export type DeliveryPayKey = "annulled" | "paid" | "byinv" | "unpaid" | "untracked";

export const payKeyOf = (delivery: Delivery): DeliveryPayKey => {
  if (isDeliveryAnnulled(delivery)) return "annulled";
  switch (delivery.paymentStatus) {
    case "paid":
      return "paid";
    case "unpaid":
      return "unpaid";
    case "by_invoice":
      return "byinv";
    default:
      return "untracked";
  }
};

export const PAYMENT_METHODS: DeliveryPaymentMethod[] = ["cash", "bank", "card"];

// Стоковата спрямо фактурата: във фактура · чака фактура · приключена без
// фактура (няма да дойде). null — не е стокова.
export type NoteInvoiceState = "inInvoice" | "waiting" | "closed";

export const noteInvoiceStateOf = (delivery: Delivery): NoteInvoiceState | null => {
  if (docTypeOf(delivery) !== "note") return null;
  if (delivery.invoiceId != null) return "inInvoice";
  return delivery.closedWithoutInvoiceAt ? "closed" : "waiting";
};

// „Фактура №123 (Метро)" / „Стокова б/н" — за потвържденията и тостовете
export const documentLabelOf = (delivery: Delivery, t: TFunction) => {
  const number = delivery.invoiceNumber
    ? `№${delivery.invoiceNumber}`
    : t("inventoryPage.noDocNumber");
  const label = t(
    docTypeOf(delivery) === "note" ? "inventoryPage.docLabelNote" : "inventoryPage.docLabelInvoice",
    { number },
  );
  return delivery.supplierName ? `${label} (${delivery.supplierName})` : label;
};

// След колко дни неплатен документ става „просрочен" (червено в Плащане)
export const OVERDUE_PAYMENT_DAYS = 14;

// Складовете на доставката се извеждат от редовете ѝ (доставка може да е
// разпределена в няколко склада → „Бар, Кухня")
export const warehouseNamesOf = (delivery: Delivery) => {
  const names = Array.from(
    new Set(
      (delivery.lines ?? [])
        .map((line) => line.warehouseName)
        .filter((name): name is string => !!name),
    ),
  );
  if (names.length) return names;
  return delivery.warehouseName ? [delivery.warehouseName] : [];
};

// Сумата, която документът носи в справката: фактура, обобщила стокови,
// сумира и тях (стоковите вътре във фактура НЕ се сумират втори път).
export const deliveryGrossOf = (delivery: Delivery) =>
  (delivery.totalValueWithVat || 0) + (delivery.linkedNotesTotalWithVat || 0);
export const deliveryNetOf = (delivery: Delivery) =>
  (delivery.totalValueNoVat || 0) + (delivery.linkedNotesTotalNoVat || 0);

// Сумите и броят редове на стоковите — фактурата ги носи в своите тотали
export const linkedNotesSummary = (notes: Delivery[]) => ({
  lines: notes.reduce((sum, note) => sum + note.lines.length, 0),
  net: notes.reduce((sum, note) => sum + deliveryNetOf(note), 0),
  gross: notes.reduce((sum, note) => sum + deliveryGrossOf(note), 0),
});

// Чиповете „общо по мерна единица" на картата „Продукти" + количествата на
// стоковите (в базови единици, както собствените редове)
export const withNoteUnitTotals = (
  unitTotals: [string, { qty: number; dec: number }][],
  notes: Delivery[],
  productById: Map<number, Product>,
  decimalsOf: (productId: number) => number,
): [string, { qty: number; dec: number }][] => {
  if (!notes.length) return unitTotals;
  const totals = new Map(unitTotals.map(([unit, value]) => [unit, { ...value }]));
  for (const line of notes.flatMap((note) => note.lines)) {
    if (line.quantity <= 0) continue;
    const unit = productById.get(line.productId)?.unit?.trim() || "—";
    const current = totals.get(unit);
    if (current) current.qty += line.quantity;
    else totals.set(unit, { qty: line.quantity, dec: decimalsOf(line.productId) });
  }
  return [...totals.entries()].sort((a, b) => b[1].qty - a[1].qty);
};
